import cx from "classnames"
import PropTypes from "prop-types"
import { Button as NextUIButton } from "@nextui-org/react"
import { NavLink } from "react-router-dom"

type Props = {
  theme?: string
  size?: string
  outline?: boolean
  solid?: boolean
  full?: boolean
  className?: string
  title?: string
  disabled?: boolean
  type?: string
  to?: string
  children: React.ReactNode
  onClick?: () => void
  onPress?: () => void
}

const Button = ({
  theme = "primary",
  size = "base",
  outline = false,
  solid = false,
  full = false,
  className,
  children,
  ...props
}: Props) => {
  const classNames = cx(
    "clickable data-[pressed=true]:scale-1 inline-flex h-auto min-w-0 items-center gap-x-2 font-bulo font-bold",
    {
      /* Solid buttons */
      "bg-red text-white hover:bg-dark-red": !outline && theme === "primary",
      "bg-blue text-dark hover:bg-mid-blue": !outline && theme === "secondary",
      /* Outline buttons */
      "border border-red bg-transparent text-ghost hover:border-dark-red hover:text-red":
        outline && theme === "primary",
      /* Blue/Red buttons */
      "border border-[#0D2748] bg-[#203048] text-[#E1EBFF] hover:border-[#264875] hover:bg-[#233856] shadow-blue-button":
        theme === "blue",
      "border border-[#480D0D] bg-[#482020] text-[#FFE1E1] hover:border-[#752626] hover:bg-[#562323] shadow-red-button":
        theme === "red",
      /* Neutral buttons */
      "shadow-inside border-earth border bg-mine-shaft text-white": theme === "neutral",
      "text-earth !font-normal hover:text-white": theme === "none",
      /* Sizes */
      "rounded-plus px-3.5 h-7 text-[10px]": size === "xxs",
      "rounded-plus px-4 h-7 text-xs": size === "xs",
      "rounded-plus px-5 h-8 text-sm": size === "sm",
      "rounded-lg px-3 py-[0.6875rem] text-sm": size === "base",
      "px-4 pb-2 pt-2 text-base": size === "lg",
      /* Full */
      "w-full": full,
    },
    className
  )

  if (props.to) {
    props.as = NavLink
  }

  return (
    <NextUIButton className={classNames} {...props}>
      {children}
    </NextUIButton>
  )
}

Button.propTypes = {
  theme: PropTypes.oneOf(["primary", "secondary", "neutral", "none", "blue", "red"]),
  size: PropTypes.oneOf(["xxs", "xs", "sm", "base", "lg"]),
  /* If true, render button outline style */
  outline: PropTypes.bool,
  /* If true, render button in full width */
  full: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
}

export default Button
